(() => {
  let catalogPromise;
  let debounce;
  const matcher = window.MayhemWheelSearch;
  if (!matcher) return;

  const getResults = () => document.querySelector('predictive-search-component #predictive-search-results');
  const getInput = () => document.querySelector('predictive-search-component input[name="q"]');

  async function catalog() {
    const finder = document.querySelector('[data-mayhem-wheel-finder][data-mwf-catalog-state="complete"] [data-mwf-data]');
    if (finder) { try { return JSON.parse(finder.textContent); } catch {} }
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async () => {
      const rows = [];
      const seen = new Set();
      let next = new URL('/search', window.location.origin);
      next.searchParams.set('section_id', 'mayhem-wheel-finder');
      if (new URL(window.location.href).searchParams.has('preview_theme_id')) {
        next.searchParams.set('preview_theme_id', new URL(window.location.href).searchParams.get('preview_theme_id'));
      }
      let pages = 0;
      while (next && pages < 100) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        let response;
        try { response = await fetch(next, { credentials: 'same-origin', signal: controller.signal }); }
        finally { clearTimeout(timeout); }
        if (!response.ok) throw new Error('Wheel lookup unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = doc.querySelector('[data-mayhem-wheel-finder]');
        if (!section) throw new Error('Wheel lookup unavailable');
        const batch = JSON.parse(section.querySelector('[data-mwf-data]').textContent);
        for (const row of batch) {
          if (seen.has(String(row.variantId))) continue;
          seen.add(String(row.variantId));
          rows.push(row);
        }
        pages += 1;
        const nextPath = section.dataset.mwfNextUrl;
        next = nextPath ? new URL(nextPath, window.location.origin) : null;
        if (next) {
          next.searchParams.set('section_id', 'mayhem-wheel-finder');
          const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (previewId) next.searchParams.set('preview_theme_id', previewId);
        }
      }
      if (next) throw new Error('Wheel lookup incomplete');
      return rows;
    })().catch((error) => { catalogPromise = null; throw error; });
    return catalogPromise;
  }

  function findMatches(rows, query) {
    matcher.registerStyles(rows);
    const parsed = matcher.parse(query);
    const exactQuery = matcher.compact(query);
    const matches = rows.filter(row => matcher.matches(row, parsed));
    matches.sort((a, b) => {
      const exactA = matcher.compact(a.sku) === exactQuery;
      const exactB = matcher.compact(b.sku) === exactQuery;
      return Number(exactB) - Number(exactA) ||
        matcher.rank(a, parsed) - matcher.rank(b, parsed) ||
        String(a.title).localeCompare(String(b.title)) ||
        String(a.sku).localeCompare(String(b.sku), undefined, { numeric: true });
    });
    const seen = new Set();
    return matches.filter(row => {
      const key = String(row.variantId || row.sku);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 6);
  }

  function render(rows, query) {
    const input = getInput();
    const results = getResults();
    if (!input || !results || input.value.trim().toLowerCase() !== query) return;
    const existing = results.querySelector('[data-mayhem-sku-suggestion]');
    const nativeExact = [...results.querySelectorAll('.mayhem-exact-sku')].some((card) => !card.closest('[data-mayhem-sku-suggestion]'));
    const shown = rows;
    const signature = `${query}:${shown.map((row) => row.variantId).join(',')}`;
    if (results.children.length === 1 && existing?.dataset.signature === signature) return;
    existing?.remove();
    const nativeEmpty = results.querySelector('.predictive-search-results__no-results');
    if (nativeEmpty) nativeEmpty.hidden = shown.length > 0;

    const status = results.querySelector('[role="status"]');
    if (status) status.textContent = `Showing ${shown.length} matching configurations for ${input.value.trim()}`;

    const group = document.createElement('div');
    group.dataset.mayhemSkuSuggestion = '';
    group.dataset.signature = signature;
    const parsed = matcher.parse(query);
    for (const row of shown) {
      const exact = matcher.compact(row.sku) === matcher.compact(query);
      const card = document.createElement('div');
      card.className = 'mayhem-exact-sku';
      card.setAttribute('aria-label', exact ? 'Exact part number match' : 'Matching wheel');
      const eyebrow = document.createElement('span');
      eyebrow.className = 'mayhem-exact-sku__eyebrow';
      eyebrow.textContent = exact ? 'Exact part number match' : 'Matching wheel';
      const link = document.createElement('a');
      link.className = 'mayhem-exact-sku__link';
      const target = new URL(row.url, window.location.origin);
      target.searchParams.set('variant', row.variantId);
      link.href = target.toString();
      if (row.image) {
        const image = document.createElement('img');
        image.className = 'mayhem-exact-sku__image';
        image.src = row.image;
        image.alt = row.title;
        link.append(image);
      }
      const details = document.createElement('span');
      details.className = 'mayhem-exact-sku__details';
      const title = document.createElement('strong');
      title.textContent = row.title;
      const specs = document.createElement('span');
      const parts = [row.finish, row.size, row.boltOption, row.offset !== null && row.offset !== undefined && row.offset !== '' ? `${row.offset} mm offset` : '', `Part # ${row.sku}`];
      if (parsed.hubBore !== null) parts.push(`Hub bore ${row.hubBore} mm`);
      if (parsed.backspace !== null) parts.push(`Backspace ${row.backspace} in`);
      specs.textContent = parts.filter(Boolean).join(' · ');
      const action = document.createElement('span');
      action.className = 'mayhem-exact-sku__action';
      action.textContent = 'View this wheel';
      details.append(title, specs, action);
      link.append(details);
      card.append(eyebrow, link);
      group.append(card);
    }
    const viewAll = document.createElement('a');
    viewAll.className = 'mayhem-search-view-all';
    const search = new URL('/search', window.location.origin);
    search.searchParams.set('type', 'product');
    search.searchParams.set('q', input.value.trim());
    const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewId) search.searchParams.set('preview_theme_id', previewId);
    viewAll.href = search.toString();
    viewAll.textContent = 'View all matching wheels';
    group.append(viewAll);
    results.replaceChildren(group);
  }

  window.MayhemSearchSuggestions = { search: async query => findMatches(await catalog(), query.toLowerCase()) };

  async function update() {
    const input = getInput();
    const query = input?.value.trim().toLowerCase() || '';
    let selectedVehicle = false;
    for (const store of ['localStorage','sessionStorage']) { try { selectedVehicle ||= /^\d+$/.test(window[store].getItem('mayhem:selected-vehicle-id') || ''); } catch {} }
    if (matcher.vehicleQuery(query)) {
      const results = getResults();
      if (!results) return;
      const signature = 'vehicle:' + query;
      if (results.children.length === 1 && results.firstElementChild?.dataset.vehicleSearch === signature) return;
      const link = document.createElement('a');
      const url = new URL('/search', location.origin);
      url.searchParams.set('q', input.value.trim()); url.searchParams.set('type','product');
      const preview = new URL(location.href).searchParams.get('preview_theme_id'); if (preview) url.searchParams.set('preview_theme_id',preview);
      link.href = url.href; link.className = 'mayhem-search-view-all'; link.dataset.vehicleSearch = signature;
      link.textContent = window.MayhemSearchLabels.vehicleResults;
      results.replaceChildren(link); return;
    }
    if (query.length < 2) {
      const results = getResults();
      results?.querySelector('[data-mayhem-sku-suggestion]')?.remove();
      const nativeEmpty = results?.querySelector('.predictive-search-results__no-results');
      if (nativeEmpty) nativeEmpty.hidden = false;
      return;
    }
    try {
      const rows = await catalog();
      render(findMatches(rows, query), query);
    } catch {
      getResults()?.querySelector('[data-mayhem-sku-suggestion]')?.remove();
    }
  }

  document.addEventListener('input', (event) => {
    if (!event.target.matches('predictive-search-component input[name="q"]')) return;
    window.clearTimeout(debounce);
    debounce = window.setTimeout(update, 250);
  });
  const observer = new MutationObserver(() => {
    if (getInput()?.value) window.setTimeout(update, 0);
  });
  window.addEventListener('mayhem:vehicle-changed', update);
  observer.observe(document.body, { childList: true, subtree: true });
})();


